import { Fragment, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

import type {
  FinancialStatements,
  StatementCategoryLines,
  StatementSection,
  StatementSectionLines,
} from "../../services/financialService";

interface Props {
  data?: FinancialStatements;
  statement: "income_expenditure" | "financial_position" | "changes_in_equity";
  /** Template headings, so an unplaced account can be filed without leaving the page. */
  sections?: StatementSection[];
  canFile?: boolean;
  onFile?: (data: { id: number; statement_category: number | "" }) => void;
}

const money = (value: string | null | undefined) => {
  const amount = Number(value ?? 0);
  // Accounting convention: a negative reads as a bracketed figure, which is
  // easier to pick out down a column of numbers than a minus sign.
  return amount < 0 ? `(${Math.abs(amount).toLocaleString()})` : amount.toLocaleString();
};

interface GroupTotal {
  label: string;
  amount: string;
  comparativeAmount: string | null;
}

function Sections({
  sections,
  comparing,
  onOpenCategory,
  totalAfterSection,
  hideSectionAmount,
}: {
  sections: StatementSectionLines[];
  comparing: boolean;
  onOpenCategory: (category: StatementCategoryLines) => void;
  /** A running total to drop in right after a given section id -- e.g. "Total
   * assets" after the last asset section -- regardless of how the school has
   * named or reordered its own sections. */
  totalAfterSection?: Record<number, GroupTotal>;
  /** Section headings (Income, Expenditure, Non-current assets, Equity, ...)
   * are pure groupings -- the categories underneath already carry the real
   * figures, so repeating a number next to the heading itself is just noise. */
  hideSectionAmount?: boolean;
}) {
  return (
    <>
      {sections.map((section) => {
        const groupTotal = totalAfterSection?.[section.id];
        return (
          <Fragment key={section.id}>
            <tbody className="border-b last:border-b-0">
              <tr className="bg-secondary-50">
                <td className="px-4 py-2 font-semibold text-gray-900" colSpan={2}>{section.name}</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums">
                  {hideSectionAmount ? "" : money(section.total)}
                </td>
                {comparing && (
                  <td className="px-4 py-2 text-right font-semibold tabular-nums text-gray-500">
                    {hideSectionAmount ? "" : money(section.comparative_total)}
                  </td>
                )}
              </tr>
              {section.categories.map((category) => (
                <tr key={category.id} className="border-t">
                  <td className="px-4 py-2 font-medium" colSpan={2}>
                    {category.accounts.length ? (
                      <button
                        type="button"
                        className="text-left text-primary-700 hover:underline"
                        onClick={() => onOpenCategory(category)}
                      >
                        {category.name}
                      </button>
                    ) : (
                      <span className="text-gray-700">{category.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums">{money(category.total)}</td>
                  {comparing && (
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                      {money(category.comparative_total)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {groupTotal && (
              <tbody className="border-b last:border-b-0">
                <tr className="border-t-2 border-gray-300 font-semibold">
                  <td className="px-4 py-2" colSpan={2}>{groupTotal.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(groupTotal.amount)}</td>
                  {comparing && (
                    groupTotal.comparativeAmount != null ? (
                      <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                        {money(groupTotal.comparativeAmount)}
                      </td>
                    ) : <td />
                  )}
                </tr>
                {/* Blank spacer row so the next section doesn't run straight
                    into a total row it isn't part of. */}
                <tr aria-hidden="true">
                  <td className="py-2" colSpan={comparing ? 4 : 3} />
                </tr>
              </tbody>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

/** One category's accounts, opened full-page instead of inlined on the
 * statement -- a category can carry well over a hundred accounts, which
 * would otherwise dwarf the statement itself. */
function CategoryDrilldown({
  category,
  comparing,
  onBack,
}: {
  category: StatementCategoryLines;
  comparing: boolean;
  onBack: () => void;
}) {
  return (
    <div className="card overflow-x-auto">
      <div className="card-content">
        <button type="button" className="text-sm text-primary-700 hover:underline" onClick={onBack}>
          ‹ Back to statement
        </button>
        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
          <h3 className="font-semibold">{category.name}</h3>
          <span className="text-sm text-gray-500">
            {category.accounts.length} account{category.accounts.length === 1 ? "" : "s"}
          </span>
        </div>
        <table className="mt-4 min-w-full">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-gray-500">
              <th className="px-4 py-2" colSpan={2}>Account</th>
              <th className="px-4 py-2 text-right">Amount</th>
              {comparing && <th className="px-4 py-2 text-right">Comparative</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {category.accounts.map((line) => (
              <tr key={line.id} className="text-sm">
                <td className="py-1.5 pl-4 pr-2 font-mono text-xs text-gray-500">{line.code}</td>
                <td className="py-1.5 pr-4">{line.name}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{money(line.amount)}</td>
                {comparing && (
                  <td className="px-4 py-1.5 text-right tabular-nums text-gray-500">
                    {money(line.comparative_amount)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-900">
            <tr className="font-semibold">
              <td className="px-4 py-3" colSpan={2}>Total</td>
              <td className="px-4 py-3 text-right tabular-nums">{money(category.total)}</td>
              {comparing && (
                <td className="px-4 py-3 text-right tabular-nums text-gray-500">
                  {money(category.comparative_total)}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function FinancialStatementsView({ data, statement, sections, canFile, onFile }: Props) {
  // A category can carry well over a hundred accounts, so opening one
  // replaces the statement with just its accounts rather than inlining them
  // all. Reset whenever the statement or its data changes, so switching
  // tabs (or refetching a new period) doesn't leave a stale drill-down up.
  const [openCategory, setOpenCategory] = useState<StatementCategoryLines | null>(null);
  useEffect(() => setOpenCategory(null), [statement, data]);

  if (!data) {
    return <div className="card"><div className="card-content text-sm text-gray-500">Loading statements…</div></div>;
  }
  const comparing = Boolean(data.comparative);
  const income = data.income_expenditure;
  const position = data.financial_position;
  const equity = data.changes_in_equity;

  if (statement === "changes_in_equity") {
    return (
      <div className="card overflow-x-auto">
        <div className="card-content">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold">{equity.title}</h3>
              <p className="text-xs text-gray-500">
                For the period {equity.start_date || "inception"} to {equity.end_date}
              </p>
            </div>
            <span className={`text-sm font-medium ${equity.agrees_with_balance_sheet ? "text-green-700" : "text-red-700"}`}>
              {equity.agrees_with_balance_sheet ? "Agrees with the balance sheet" : "Does not agree with the balance sheet"}
            </span>
          </div>
          <table className="mt-4 min-w-full">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-2" colSpan={2}>Fund</th>
                <th className="px-4 py-2 text-right">Opening</th>
                <th className="px-4 py-2 text-right">Movement</th>
                <th className="px-4 py-2 text-right">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {equity.accounts.map((row) => (
                <tr key={row.id}>
                  <td className="py-2 pl-4 pr-2 font-mono text-xs text-gray-500">{row.code}</td>
                  <td className="py-2 pr-4">{row.name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(row.opening)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(row.movement)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(row.closing)}</td>
                </tr>
              ))}
              <tr>
                <td className="py-2 pl-4 pr-4" colSpan={2}>
                  Retained surplus / (deficit)
                  <span className="block text-xs text-gray-500">
                    The movement here is the surplus from the income statement.
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{money(equity.opening_surplus)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{money(equity.surplus)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{money(equity.closing_surplus)}</td>
              </tr>
            </tbody>
            <tfoot className="border-t-2 border-gray-900">
              <tr className="font-semibold">
                <td className="px-4 py-3" colSpan={2}>Total funds</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(equity.opening_funds)}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {money(String(Number(equity.closing_funds) - Number(equity.opening_funds)))}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{money(equity.closing_funds)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  const report = data[statement];

  if (openCategory) {
    return <CategoryDrilldown category={openCategory} comparing={comparing} onBack={() => setOpenCategory(null)} />;
  }

  // Assets, then equity, then liabilities -- what the school owns, what it's
  // worth, and only then what it owes -- regardless of whatever order the
  // sections happen to be filed in under the template. Each group's total is
  // dropped in right after it, using the accounts' own types (position.assets
  // etc.), same as the "Balanced" check above, rather than re-summing the
  // section totals shown, so an account not yet filed under any section
  // still counts here even though it's absent from the sections below.
  let displaySections = report.sections;
  let totalAfterSection: Record<number, GroupTotal> | undefined;
  if (statement === "financial_position") {
    const typeOf = (id: number) => sections?.find((s) => s.id === id)?.account_types?.[0];
    const byType = (type: string) => report.sections.filter((section) => typeOf(section.id) === type);
    const assetSections = byType("asset");
    const equitySections = byType("equity");
    const liabilitySections = byType("liability");
    const classifiedIds = new Set([...assetSections, ...equitySections, ...liabilitySections].map((s) => s.id));
    const otherSections = report.sections.filter((section) => !classifiedIds.has(section.id));
    displaySections = [...assetSections, ...equitySections, ...liabilitySections, ...otherSections];

    const lastId = (list: StatementSectionLines[]) => list[list.length - 1]?.id;
    totalAfterSection = {};
    const assetsSectionId = lastId(assetSections);
    const equitySectionId = lastId(equitySections);
    const liabilitiesSectionId = lastId(liabilitySections);
    if (assetsSectionId !== undefined) {
      totalAfterSection[assetsSectionId] = { label: "Total assets", amount: position.assets, comparativeAmount: null };
    }
    if (equitySectionId !== undefined) {
      totalAfterSection[equitySectionId] = { label: "Total equity", amount: position.equity, comparativeAmount: null };
    }
    if (liabilitiesSectionId !== undefined) {
      totalAfterSection[liabilitiesSectionId] = { label: "Total liabilities", amount: position.liabilities, comparativeAmount: null };
    }
  }

  return (
    <div className="space-y-4">
      <div className="card overflow-x-auto">
        <div className="card-content">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold">{report.title}</h3>
              <p className="text-xs text-gray-500">
                {statement === "income_expenditure"
                  ? `For the period ${income.start_date || "inception"} to ${income.end_date}`
                  : `As at ${position.as_at}`}
              </p>
            </div>
            {statement === "financial_position" && (
              <span className={`text-sm font-medium ${position.is_balanced ? "text-green-700" : "text-red-700"}`}>
                {position.is_balanced ? "Balanced" : "Out of balance"}
              </span>
            )}
          </div>

          <table className="mt-4 min-w-full">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-2" colSpan={2} />
                <th className="px-4 py-2 text-right">Amount</th>
                {comparing && <th className="px-4 py-2 text-right">Comparative</th>}
              </tr>
            </thead>
            <Sections
              sections={displaySections}
              comparing={comparing}
              onOpenCategory={setOpenCategory}
              totalAfterSection={totalAfterSection}
              hideSectionAmount
            />
            <tfoot className="border-t-2 border-gray-900">
              {statement === "income_expenditure" ? (
                <tr className="font-semibold">
                  <td className="px-4 py-3" colSpan={2}>Surplus / (deficit) for the period</td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(income.surplus)}</td>
                  {comparing && <td />}
                </tr>
              ) : (
                <>
                  <tr>
                    <td className="px-4 py-2 text-gray-700" colSpan={2}>Accumulated surplus / (deficit)</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(position.accumulated_surplus)}</td>
                    {comparing && <td />}
                  </tr>
                  <tr className="border-t-2 border-gray-300 font-semibold">
                    <td className="px-4 py-3" colSpan={2}>Total equity and liabilities</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(position.funded_by)}</td>
                    {comparing && <td />}
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {data.unclassified.count > 0 && (
        <div className="card border border-amber-300">
          <div className="card-content">
            <h4 className="flex items-center gap-2 font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              {data.unclassified.count} account{data.unclassified.count === 1 ? "" : "s"} not yet placed in the template
            </h4>
            <p className="mt-1 text-sm text-amber-800">
              These are main accounts that appear on neither statement, whether or not they
              carry a balance yet. {canFile
                ? "Place each one below and it moves onto the statement straight away."
                : "Someone who can configure the accounts needs to place them."} Until then they are
              listed here rather than quietly left out.
            </p>
            <table className="mt-3 min-w-full text-sm">
              <tbody className="divide-y">
                {data.unclassified.accounts.map((line) => (
                  <tr key={line.id}>
                    <td className="py-1.5 pr-4 font-mono text-xs text-gray-500">{line.code}</td>
                    <td className="py-1.5 pr-4">{line.name}</td>
                    <td className="py-1.5 pr-4 capitalize text-gray-500">{line.account_type}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">{money(line.amount)}</td>
                    {canFile && (
                      <td className="py-1.5 pl-4">
                        <select
                          className="input py-1 text-sm"
                          value=""
                          aria-label={`Where ${line.name} reports`}
                          onChange={(event) => {
                            if (event.target.value) {
                              onFile?.({ id: line.id, statement_category: Number(event.target.value) });
                            }
                          }}
                        >
                          <option value="">Place it…</option>
                          {(sections ?? [])
                            .filter((section) => section.permitted_account_types.includes(line.account_type))
                            .map((section) => (
                              <optgroup key={section.id} label={section.name}>
                                {section.categories.map((category) => (
                                  <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                              </optgroup>
                            ))}
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
